import { Address } from "@rumble-it/common-lib/types/common/Address.interface";

/**
 * Generates a Google Maps directions link for a given service partner address.
 * @param address An object containing street, streetNumber, streetNumberAddition, postalCode, and city.
 * @returns A Google Maps link with the address as the destination.
 */
export type MapsProvider = "google" | "bing" | "apple";

/**
 * Generates a directions link for a given service partner address and map provider.
 * @param address An object containing street, streetNumber, streetNumberAddition, postalCode, and city.
 * @param provider The map provider to use ('google', 'bing', 'apple'). Defaults to 'google'.
 * @returns A maps link with the address as the destination.
 */

export interface GetMapsLinkProps {
  address: Address;
  provider?: MapsProvider;
}

/**
 * Generates a directions link for a given service partner address and map provider.
 * @param props Object containing address and optional provider.
 * @returns A maps link with the address as the destination.
 */
export function getMapsLink({
  address,
  provider = "google",
}: GetMapsLinkProps): string {
  if (!address) return "";
  const parts = [
    address.street || "",
    address.streetNumber || "",
    address.streetNumberAddition || "",
    ",",
    address.postalCode || "",
    address.city || "",
  ];
  const destination = parts.join(" ").replace(/ +/g, " ").trim();
  const encoded = encodeURIComponent(destination);
  switch (provider) {
    case "bing":
      return `https://www.bing.com/maps?rtp=~adr.${encoded}`;
    case "apple":
      return `https://maps.apple.com/?daddr=${encoded}`;
    default:
      return `https://www.google.nl/maps?saddr=&daddr=${encoded}`;
  }
}
